import jwtDefaultConfig from './jwtDefaultConfig'
import { getSessionToken } from './tokenHolder'

/**
 * PROP-086: токен сессии больше НЕ хранится в localStorage (уводился любым XSS, а на платформе
 * есть пользовательский HTML/виджеты) и вообще не проходит через этот сервис. Браузер→API
 * аутентифицируется httpOnly-cookie (её ставит и гасит бэк, из JS недоступна, уходит сама
 * благодаря withCredentials). Поэтому здесь нет ни запросного интерсептора с Bearer-заголовком,
 * ни чтения/записи токена: сервис — это набор эндпоинт-методов плюс обработка 401.
 *
 * Токен в JS остаётся только В ПАМЯТИ, в Vuex-сессии, и только там, где он реально нужен —
 * AI-компоненты (chatbot/constructor/ai-assistant) отдают его вспомогательному AI-сервису на
 * ДРУГОМ origin, куда cookie не приложится. Эти компоненты читают токен из Vuex сами
 * (`this.$store.getters['cubus-store/SESSION'].token`). При перезагрузке страницы память пуста,
 * и сессия восстанавливается запросом `whoami` по cookie (router guard) — токен приходит обратно.
 *
 * Мёртвый refresh-флоу (`/jwt/refresh-token`, роута на бэке нет) убран: на 401 просто чистим и
 * уводим на логин, без лишнего круга к несуществующему эндпоинту.
 */
export default class JwtService {
  axiosIns = null

  jwtConfig = { ...jwtDefaultConfig }

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // PROP-086: Bearer-заголовок из токена В ПАМЯТИ (не из localStorage). Cookie тоже уходит
    // (withCredentials) — это дублирование: заголовочный путь работает внутри активной сессии,
    // cookie переживает перезагрузку. Токена нет (до логина / после перезагрузки до whoami) —
    // заголовок не ставим, аутентифицирует cookie.
    this.axiosIns.interceptors.request.use(
      config => {
        if (!this.isAuthFree(config && config.url)) {
          const t = this.getToken()
          if (t) {
            config.headers = config.headers || {}
            config.headers.Authorization = `${this.jwtConfig.tokenType} ${t}`
          }
        }
        return config
      },
      error => Promise.reject(error),
    )

    this.axiosIns.interceptors.response.use(
      r => r,
      error => {
        const { config, response } = error
        if (response && response.status === 401 && !this.isAuthFree(config && config.url)) {
          this.clearAuth()
          window.location.assign(this.jwtConfig.loginRoute || '/login')
        }
        return Promise.reject(error)
      },
    )
  }

  isAuthFree(url) {
    const u = String(url || '')
    const e = this.jwtConfig
    return [
      e.loginEndpoint,
      e.registerEndpoint,
      e.forgotEndpoint,
      e.whoamiEndpoint,
      '/api/release',
    ].some(x => u.endsWith(x))
  }

  // PROP-086: токен читается из держателя В ПАМЯТИ (не из localStorage). Единый источник для
  // интерсептора, body-параметров, гейтов авторизации, CASL, HTML-SDK и AI-сервиса.
  // eslint-disable-next-line class-methods-use-this
  getToken() {
    return getSessionToken()
  }

  // eslint-disable-next-line class-methods-use-this
  getCubusToken() {
    return getSessionToken()
  }

  // Записи-заглушки: токен намеренно НЕ кладётся в localStorage; носитель — держатель в памяти
  // (его выставляет мутация SET_SESSION) и httpOnly-cookie. Методы оставлены, чтобы старые вызовы
  // (cubusService, CbsLogin, CbsRegister, вендорные Login/Register) не падали.
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  setToken(value) {}

  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  setCubusToken(value) {}

  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  setRefreshToken(value) {}

  clearAuth() {
    // Чистим возможные ЛЕГАСИ-ключи прошлых версий: у кого токен ещё лежит в localStorage —
    // убираем при первом же разлогине/401. Сессию в памяти сбрасывает переход на логин ниже.
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageCubusTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    delete this.axiosIns.defaults.headers.common.Authorization
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  forgot(...args) {
    return this.axiosIns.post(this.jwtConfig.forgotEndpoint, ...args)
  }

  whoami(...args) {
    return this.axiosIns.post(this.jwtConfig.whoamiEndpoint, ...args)
  }

  logout(...args) {
    return this.axiosIns.post(this.jwtConfig.logoutEndpoint, ...args)
  }

  changePassword(...args) {
    return this.axiosIns.post(this.jwtConfig.changepassEndpoint, ...args)
  }

  query(...args) {
    return this.axiosIns.post(this.jwtConfig.queryEndpoint, ...args)
  }

  // PROP-230: токен доступа. `action`: issue | info | revoke.
  apiToken(...args) {
    return this.axiosIns.post(this.jwtConfig.apiTokenEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  presignUpload(data) {
    return this.axiosIns.post(this.jwtConfig.presignUploadEndpoint, data)
  }

  upload(...args) {
    return this.axiosIns.post(this.jwtConfig.uploadEndpoint, ...args)
  }

  uploadChunk(...args) {
    return this.axiosIns.post(this.jwtConfig.uploadchunkEndpoint, ...args)
  }

  download(...args) {
    return this.axiosIns.post(this.jwtConfig.downloadEndpoint, ...args, { responseType: 'blob' })
      .then(response => {
        if (response.data && response.data.type === 'application/json') {
          return response.data.text().then(text => {
            const json = JSON.parse(text)
            if (json.s3url) {
              window.location.href = json.s3url
              return { ...response, s3redirect: true }
            }
            return response
          })
        }
        return response
      })
  }
}
